
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Faq, FaqFormData, FaqStatus } from "@/types/faq-types";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Image as ImageIcon, Video } from "lucide-react";
import FaqPreview from "./FaqPreview";
import SimpleFaqEditor from "./SimpleFaqEditor";

interface FaqFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  faq: Faq | null;
  onSubmit: (data: FaqFormData) => Promise<boolean>;
  isSubmitting: boolean;
  uploadMedia: (file: File) => Promise<string | null>;
}

const FaqFormDialog = ({
  open,
  onOpenChange,
  faq,
  onSubmit,
  isSubmitting,
  uploadMedia
}: FaqFormDialogProps) => {
  const [activeTab, setActiveTab] = useState("content");
  const [isUploading, setIsUploading] = useState(false);

  // Form state
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<FaqStatus>("draft");
  const [displayOrder, setDisplayOrder] = useState(0);
  const [active, setActive] = useState(true);

  // Initialize form with FAQ data if editing
  useEffect(() => {
    if (faq) {
      setQuestion(faq.question);
      setAnswer(faq.answer);
      setImageUrl(faq.image_url || null);
      setVideoUrl(faq.video_url || null);
      setStatus(faq.status);
      setDisplayOrder(faq.display_order);
      setActive(faq.active);
    } else {
      // Default values for new FAQ
      setQuestion("");
      setAnswer("");
      setImageUrl(null);
      setVideoUrl(null);
      setStatus("draft");
      setDisplayOrder(0);
      setActive(true);
    }
  }, [faq]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await uploadMedia(file);
      if (url) {
        setImageUrl(url);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!question.trim()) {
      alert("Question is required");
      return;
    }

    if (!answer.trim()) {
      alert("Answer is required");
      return;
    }

    const formData: FaqFormData = {
      question,
      answer,
      image_url: imageUrl,
      video_url: videoUrl,
      status,
      display_order: displayOrder,
      active
    };

    const success = await onSubmit(formData);
    if (success) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{faq ? "Edit FAQ" : "Add New FAQ"}</DialogTitle>
          <DialogDescription>
            {faq ? "Update the FAQ details below." : "Fill in the details to create a new FAQ."}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="space-y-4 pt-4">
            <form id="faq-form" onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="question">Question</Label>
                <Input
                  id="question"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Enter the question"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="answer">Answer</Label>
                <SimpleFaqEditor
                  value={answer}
                  onChange={setAnswer}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Use the toolbar to add formatting to your answer.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={status} onValueChange={(value) => setStatus(value as FaqStatus)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="displayOrder">Display Order</Label>
                  <Input
                    id="displayOrder"
                    type="number"
                    value={displayOrder}
                    onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                    min={0}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="active" className="flex items-center gap-2">
                  <span>Active</span>
                  <Switch id="active" checked={active} onCheckedChange={setActive} />
                </Label>
                <p className="text-sm text-muted-foreground">
                  Inactive FAQs won't be displayed on the website.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="imageUpload" className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  <span>Image (Optional)</span>
                </Label>
                <div className="flex items-center gap-4">
                  <Input
                    id="imageUpload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isUploading}
                    className="max-w-xs"
                  />
                  {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
                </div>
                {imageUrl && (
                  <div className="mt-2">
                    <img src={imageUrl} alt="FAQ illustration" className="max-h-32 rounded border p-1" />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="mt-1 text-destructive"
                      onClick={() => setImageUrl(null)}
                      type="button"
                    >
                      Remove image
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="videoUrl" className="flex items-center gap-2">
                  <Video className="h-4 w-4" />
                  <span>YouTube Video URL (Optional)</span>
                </Label>
                <Input
                  id="videoUrl"
                  value={videoUrl || ""}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="e.g., https://www.youtube.com/watch?v=..."
                />
                <p className="text-xs text-muted-foreground">
                  Enter the full YouTube URL. Example: https://www.youtube.com/watch?v=XXXXXXXXXXX
                </p>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="preview" className="space-y-4 pt-4">
            <div className="border rounded-lg p-4">
              <FaqPreview
                question={question}
                answer={answer}
                imageUrl={imageUrl}
                videoUrl={videoUrl}
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            form="faq-form"
            disabled={isSubmitting || isUploading}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {faq ? "Updating..." : "Creating..."}
              </>
            ) : (
              faq ? "Update FAQ" : "Create FAQ"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FaqFormDialog;
